import { useEffect, useMemo, useState } from 'react';
import { Button } from './Button';
import { useEngagementStore } from '../store/engagementStore';
import { WishItem, toWishItems, WishCard } from '../hubs/people/engagement/wishKit';
import { PartyPopper, X } from 'lucide-react';

/**
 * Celebrations on the dashboard — shown to every employee.
 *
 * Reads /people/engagement/today (best-effort). Lays the wishes out horizontally:
 * one card fills the row, two sit side by side, and three or more stack in a
 * vertically scrolling list so the dashboard stays compact. Dismissible for the
 * day via localStorage.
 */

const todayKey = () => `teamone.celebrations.dismissed.${new Date().toISOString().slice(0, 10)}`;

export function CelebrationsBanner({ bare = false }: { bare?: boolean } = {}) {
  const { today, fetchToday } = useEngagementStore();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => { fetchToday(); }, [fetchToday]);
  useEffect(() => {
    try { if (localStorage.getItem(todayKey())) setDismissed(true); } catch { /* ignore */ }
  }, []);

  const cards = useMemo<WishItem[]>(() => toWishItems(today), [today]);
  const count = cards.length;

  if (dismissed || count === 0) return null;

  const dismiss = () => {
    try { localStorage.setItem(todayKey(), '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  // 1 → full width · 2 → side by side · 3+ → stacked, scrolls vertically.
  const gridCols = count === 2 ? 'sm:grid-cols-2' : 'grid-cols-1';
  const scroll = count > 2 ? 'max-h-[32rem] overflow-y-auto pr-1' : '';

  return (
    <div className={bare ? '' : 'px-3 pt-3 sm:px-4 md:px-6'}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 font-display text-sm font-semibold text-foreground">
          <PartyPopper className="size-4 text-primary" /> Celebrations today
          {count > 1 && <span className="text-xs font-normal text-muted-foreground">· {count}</span>}
        </h3>
        <Button variant="ghost" size="icon-sm" aria-label="Dismiss for today" onClick={dismiss}>
          <X className="size-4" />
        </Button>
      </div>

      <div className={`grid gap-3 ${gridCols} ${scroll}`}>
        {cards.map((item) => <WishCard key={item.key} item={item} compact />)}
      </div>
    </div>
  );
}
